import { useState, forwardRef, type ReactNode, type KeyboardEvent } from 'react';
import styles from './Input.module.css';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'search' | 'number' | 'tel' | 'url' | 'color' | 'date';
  placeholder?: string;
  size?: InputSize;
  /** naked: no container styling — transparent bg, no border. For use inside FieldRow or similar. */
  variant?: 'default' | 'naked';
  disabled?: boolean;
  error?: boolean | string;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  fullWidth?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  label?: string;
  helperText?: string;
  min?: number;
  max?: number;
  step?: number | 'any';
  /** Tope de caracteres, igual que en Textarea. El navegador corta al
   *  escribir — así el límite se siente antes de mandar, en vez de volver
   *  como un error del servidor cuando ya se perdió el resto del texto. */
  maxLength?: number;
  required?: boolean;
  weight?: 'normal' | 'medium' | 'bold';
}

const H:  Record<InputSize, string> = { sm: '28px', md: '32px', lg: '40px' };
const FS: Record<InputSize, string> = { sm: '12px', md: '13px', lg: '14px' };
const PX: Record<InputSize, string> = { sm: '8px',  md: '10px', lg: '12px' };
const FW: Record<'normal' | 'medium' | 'bold', string> = {
  normal: 'var(--evo-fw-normal)',
  medium: 'var(--evo-fw-medium)',
  bold:   'var(--evo-fw-semibold)',
};

const LABEL_STYLE = {
  display: 'block' as const,
  marginBottom: '4px',
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
} as const;

const HELPER_STYLE = {
  display: 'block' as const,
  marginTop: '4px',
  fontSize: '11px',
  fontFamily: 'var(--evo-font)',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({
  value,
  onChange,
  type = 'text',
  placeholder,
  size = 'md',
  variant = 'default',
  disabled = false,
  error,
  iconBefore,
  iconAfter,
  onKeyDown,
  onBlur,
  onFocus,
  fullWidth = false,
  autoFocus = false,
  autoComplete,
  label,
  helperText,
  min,
  maxLength,
  max,
  step,
  required,
  weight = 'normal',
}, ref) {
  const [focused, setFocused] = useState(false);

  if (variant === 'naked') {
    return (
      <input
        className={styles.input}
        ref={ref}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        min={min}
        maxLength={maxLength}
        max={max}
        step={step}
        required={required}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          // Bug real (2026-08-12): "flex:1" no alcanza cuando el padre
          // directo no es display:flex (ej. ContactAutocomplete envuelve
          // este input en un div normal para poder posicionar el dropdown
          // de sugerencias encima) — el input caía al ancho default del
          // navegador (~20 caracteres) y To/Cc/Bcc se veían cortados aunque
          // sobrara espacio. width:100% funciona en cualquier contenedor,
          // flex o no.
          flex: 1,
          width: '100%',
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--evo-fg-2)',
          fontSize: FS[size],
          fontFamily: 'var(--evo-font)',
          fontWeight: FW[weight],
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.5 : 1,
        }}
      />
    );
  }

  const hasError = !!error;
  const errorMsg = typeof error === 'string' ? error : undefined;
  const borderColor = hasError ? 'var(--evo-danger)' : focused ? 'var(--evo-cyan)' : 'var(--evo-stroke)';
  const shadow      = focused ? `0 0 0 1px ${hasError ? 'var(--evo-danger)' : 'var(--evo-cyan)'}` : 'none';

  return (
    <div style={{ display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
      {label != null && <label style={LABEL_STYLE}>{label}</label>}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          height: H[size],
          width: fullWidth ? '100%' : 'auto',
          paddingLeft: type === 'color' ? '2px' : PX[size],
          paddingRight: type === 'color' ? '2px' : PX[size],
          background: 'var(--evo-bg-3)',
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--evo-r)',
          boxShadow: shadow,
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 120ms linear, box-shadow 120ms linear',
          boxSizing: 'border-box',
        }}
      >
        {iconBefore != null && (
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
            {iconBefore}
          </span>
        )}
        <input
          className={styles.input}
          ref={ref}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          min={min}
          maxLength={maxLength}
          max={max}
          step={step}
          required={required}
          onChange={e => onChange(e.target.value)}
          onFocus={() => { setFocused(true); onFocus?.(); }}
          onBlur={() => { setFocused(false); onBlur?.(); }}
          onKeyDown={onKeyDown}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--evo-fg-2)',
            fontSize: FS[size],
            fontFamily: 'var(--evo-font)',
            fontWeight: FW[weight],
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        {iconAfter != null && (
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
            {iconAfter}
          </span>
        )}
      </div>
      {(helperText != null || errorMsg != null) && (
        <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
          {errorMsg ?? helperText}
        </span>
      )}
    </div>
  );
});
